import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO_URL, NAV_LINKS, CONTACT } from "@/lib/constants";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-brand-cream/95 backdrop-blur-sm border-b border-brand-clay shadow-sm" : "bg-brand-cream/80"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          onClick={() => handleClick("hero")}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          data-testid="navbar-logo"
          aria-label="Reading Physio home"
        >
          <img src={LOGO_URL} alt="Reading Physio logo" className="h-12 md:h-14 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => handleClick(l.id)}
              className="text-base font-medium text-slate-700 hover:text-brand-blue transition-colors duration-300"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${CONTACT.phone}`}
            className="inline-flex items-center gap-2 text-brand-blue font-semibold hover:text-brand-terracotta transition-colors"
            data-testid="navbar-phone-link"
          >
            <Phone size={18} strokeWidth={1.8} />
            <span className="hidden xl:inline">{CONTACT.phoneDisplay}</span>
          </a>
          <Button
            onClick={() => handleClick("contact")}
            className="bg-brand-blue hover:bg-brand-blue/90 text-white rounded-full px-6 h-12 text-base font-semibold"
            data-testid="navbar-book-cta"
          >
            Book Assessment
          </Button>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-brand-blue"
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-brand-cream border-t border-brand-clay" data-testid="navbar-mobile-menu">
          <nav className="px-6 py-6 flex flex-col gap-2" aria-label="Mobile primary">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => handleClick(l.id)}
                className="text-left py-3 px-3 rounded-lg text-lg font-medium text-slate-700 hover:bg-brand-sand transition-colors"
                data-testid={`mobile-nav-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <a
                href={`tel:${CONTACT.phone}`}
                className="inline-flex items-center justify-center gap-2 h-12 rounded-full border-2 border-brand-blue text-brand-blue font-semibold"
                data-testid="mobile-phone-link"
              >
                <Phone size={18} /> {CONTACT.phoneDisplay}
              </a>
              <Button
                onClick={() => handleClick("contact")}
                className="bg-brand-blue hover:bg-brand-blue/90 text-white rounded-full h-12 text-base font-semibold"
                data-testid="mobile-book-cta"
              >
                Book Assessment
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
